/**
 * Main configuration file for republic-specific settings.
 * This file serves as a barrel export, assembling configurations from section-based files.
 *
 * All existing imports will continue to work without changes.
 */

import type { Repub } from '@cx-modules/constants';

import { REPUB_CONFIG as PLUS_REPUB_CONFIG } from '~/apps/product-plus/repub-config/plus-tools-repub-config';

import { FAVICON_CONFIGS } from './favicon-config';
import { TRACKING_CONFIGS } from './tracking-config';
import type { RepubConfig, SupportedRepubName } from './types';

// Re-export all types
export type { RepubConfig, SupportedRepubName } from './types';

// Re-export OddscheckerPlus types and constants for backward compatibility
export type { PlusToolName, RepubConfig as PlusRepubConfig } from '~/apps/product-plus/repub-config/types';
export { STRIPE_PRODUCT_IDS, type ProductId } from '~/apps/product-plus/repub-config/stripe-constants';

/**
 * Main configuration object mapping republic names to their configurations
 * Assembled from individual section configs
 *
 * Note: Typed as Partial<Record<Repub, RepubConfig>> to allow indexing with any Repub value,
 * but only contains configurations for SupportedRepubName values.
 */
export const REPUB_CONFIG: Partial<Record<Repub, RepubConfig>> & Record<SupportedRepubName, RepubConfig> = {
  WS: {
    favicon: FAVICON_CONFIGS.WS,
    plus: PLUS_REPUB_CONFIG.WS,
    tracking: TRACKING_CONFIGS.WS,
  },
  OC: {
    favicon: FAVICON_CONFIGS.OC,
    plus: PLUS_REPUB_CONFIG.OC,
    tracking: TRACKING_CONFIGS.OC,
  },
  US: {
    favicon: FAVICON_CONFIGS.US,
    plus: PLUS_REPUB_CONFIG.US,
    tracking: TRACKING_CONFIGS.US,
  },
  GZ: {
    favicon: FAVICON_CONFIGS.GZ,
    plus: PLUS_REPUB_CONFIG.GZ,
    tracking: TRACKING_CONFIGS.GZ,
  },
};

export function isSupportedRepub(repub: string): repub is SupportedRepubName {
  return Object.keys(REPUB_CONFIG).includes(repub);
}
